/**
 * Assets Redux Sagas
 */

import { call, put, takeLatest, all, select, type SagaReturnType } from 'redux-saga/effects'
import type { PayloadAction } from '@reduxjs/toolkit'
import { extractErrorMessage } from '@spcc/core'
import { assetsApi } from '../infrastructure/api/assets-api'
import { driversApi } from '../infrastructure/api/drivers-api'
import { fleetApi } from '../infrastructure/api/fleet-api'
import { resourcesApi } from '../infrastructure/api/resources-api'
import { trailersApi } from '../infrastructure/api/trailers-api'
import { vehiclesApi } from '../infrastructure/api/vehicles-api'
import { assetsActions } from './assets.slice'
import { selectResourcesFilter } from './assets.selectors'
import type { DeactivationResult } from '../api/api-types'
import type {
  Asset,
  AssetsFilter,
  Driver,
  DriversFilter,
  Resource,
  ResourcesFilter,
  Trailer,
  TrailersFilter,
  Vehicle,
  VehiclesFilter,
  PaginatedDrivers,
  PaginatedVehicles,
  PaginatedTrailers,
  PaginatedResources,
} from '../domain/types'

// ============================================================================
// Legacy Assets Sagas
// ============================================================================

function* fetchAssetsSaga(action: PayloadAction<AssetsFilter | undefined>) {
  try {
    const assets: Asset[] = yield call(assetsApi.getAssets, action.payload)
    yield put(assetsActions.fetchAssetsSuccess(assets))
  } catch (error) {
    yield put(assetsActions.fetchAssetsFailure(extractErrorMessage(error)))
  }
}

// ============================================================================
// Fleet Stats Saga
// ============================================================================

function* fetchFleetStatsSaga() {
  try {
    const stats: SagaReturnType<typeof fleetApi.getFleetStats> = yield call(fleetApi.getFleetStats)
    yield put(assetsActions.fetchStatsSuccess(stats))
  } catch (error) {
    yield put(assetsActions.fetchStatsFailure(extractErrorMessage(error)))
  }
}

// ============================================================================
// Drivers Sagas
// ============================================================================

function* fetchDriversSaga(action: PayloadAction<DriversFilter | undefined>) {
  try {
    const result: PaginatedDrivers = yield call(driversApi.getDriversPaginated, action.payload)
    yield put(assetsActions.fetchDriversSuccess(result))
  } catch (error) {
    yield put(assetsActions.fetchDriversFailure(extractErrorMessage(error)))
  }
}

function* fetchDriverSaga(action: PayloadAction<string>) {
  try {
    const driver: SagaReturnType<typeof driversApi.getDriver> = yield call(
      driversApi.getDriver,
      action.payload
    )
    if (driver) {
      yield put(assetsActions.fetchDriverSuccess(driver))
    } else {
      yield put(assetsActions.fetchDriverFailure('assets.errors.driverNotFound'))
    }
  } catch (error) {
    yield put(assetsActions.fetchDriverFailure(extractErrorMessage(error)))
  }
}

function* createDriverSaga(action: PayloadAction<Partial<Driver> & { tenantId: string }>) {
  try {
    const driver: SagaReturnType<typeof driversApi.createDriver> = yield call(
      driversApi.createDriver,
      action.payload
    )
    yield put(assetsActions.createDriverSuccess(driver))
    yield put(assetsActions.fetchStats())
  } catch (error) {
    yield put(assetsActions.createDriverFailure(extractErrorMessage(error)))
  }
}

function* updateDriverSaga(
  action: PayloadAction<{ documentId: string; data: Partial<Driver> }>
) {
  try {
    const result: SagaReturnType<typeof driversApi.updateDriver> = yield call(
      driversApi.updateDriver,
      action.payload.documentId,
      action.payload.data
    )
    yield put(assetsActions.updateDriverSuccess(result.entity))
    yield put(assetsActions.fetchStats())
    if (result.deactivation.affectedOrders.length > 0) {
      yield put(assetsActions.showDeactivationWarning({
        affectedOrders: result.deactivation.affectedOrders,
        affectedResources: result.deactivation.affectedResources,
        entityType: 'driver',
        entityName: result.entity.name ?? null,
      }))
    }
  } catch (error) {
    yield put(assetsActions.updateDriverFailure(extractErrorMessage(error)))
  }
}

function* deleteDriverSaga(action: PayloadAction<string>) {
  try {
    yield call(driversApi.deleteDriver, action.payload)
    yield put(assetsActions.deleteDriverSuccess(action.payload))
    yield put(assetsActions.fetchStats())
  } catch (error) {
    yield put(assetsActions.deleteDriverFailure(extractErrorMessage(error)))
  }
}

// ============================================================================
// Resources Sagas
// ============================================================================

function* fetchResourcesSaga(action: PayloadAction<ResourcesFilter | undefined>) {
  try {
    const result: PaginatedResources = yield call(
      resourcesApi.getResourcesPaginated,
      action.payload
    )
    yield put(assetsActions.fetchResourcesSuccess(result))
  } catch (error) {
    yield put(assetsActions.fetchResourcesFailure(extractErrorMessage(error)))
  }
}

function* fetchResourceSaga(action: PayloadAction<string>) {
  try {
    const resource: SagaReturnType<typeof resourcesApi.getResource> = yield call(
      resourcesApi.getResource,
      action.payload
    )
    if (resource) {
      yield put(assetsActions.fetchResourceSuccess(resource))
    } else {
      yield put(assetsActions.fetchResourceFailure('assets.errors.resourceNotFound'))
    }
  } catch (error) {
    yield put(assetsActions.fetchResourceFailure(extractErrorMessage(error)))
  }
}

function* createResourceSaga(action: PayloadAction<Partial<Resource> & { tenantId: string }>) {
  try {
    const created: SagaReturnType<typeof resourcesApi.createResource> = yield call(
      resourcesApi.createResource,
      action.payload
    )
    let resource = created
    if (created.documentId) {
      const fetched: SagaReturnType<typeof resourcesApi.getResource> = yield call(
        resourcesApi.getResource,
        created.documentId
      )
      if (fetched) resource = fetched
    }
    yield put(assetsActions.createResourceSuccess(resource))
    yield put(assetsActions.fetchStats())
  } catch (error) {
    yield put(assetsActions.createResourceFailure(extractErrorMessage(error)))
  }
}

function* updateResourceSaga(
  action: PayloadAction<{ documentId: string; data: Partial<Resource> }>
) {
  try {
    const result: SagaReturnType<typeof resourcesApi.updateResource> = yield call(
      resourcesApi.updateResource,
      action.payload.documentId,
      action.payload.data
    )
    let resource = result.entity
    const fetched: SagaReturnType<typeof resourcesApi.getResource> = yield call(
      resourcesApi.getResource,
      action.payload.documentId
    )
    if (fetched) resource = fetched
    yield put(assetsActions.updateResourceSuccess(resource))
    yield put(assetsActions.fetchStats())
    if (result.deactivation.affectedOrders.length > 0) {
      yield put(assetsActions.showDeactivationWarning({
        affectedOrders: result.deactivation.affectedOrders,
        affectedResources: result.deactivation.affectedResources,
        entityType: 'resource',
        entityName: resource.name ?? null,
      }))
    }
  } catch (error) {
    yield put(assetsActions.updateResourceFailure(extractErrorMessage(error)))
  }
}

function* deleteResourceSaga(action: PayloadAction<string>) {
  try {
    const result: SagaReturnType<typeof resourcesApi.deleteResource> = yield call(
      resourcesApi.deleteResource,
      action.payload
    )
    yield put(assetsActions.deleteResourceSuccess(action.payload))
    const resourcesFilter: ResourcesFilter = yield select(selectResourcesFilter)
    yield put(assetsActions.fetchResources(resourcesFilter))
    yield put(assetsActions.fetchStats())
    if (result.affectedOrders.length > 0) {
      yield put(assetsActions.showDeactivationWarning({
        affectedOrders: result.affectedOrders,
        affectedResources: [],
        entityType: 'resource',
        entityName: null,
      }))
    }
  } catch (error) {
    yield put(assetsActions.deleteResourceFailure(extractErrorMessage(error)))
  }
}

// ============================================================================
// Trailers Sagas
// ============================================================================

function* fetchTrailersSaga(action: PayloadAction<TrailersFilter | undefined>) {
  try {
    const result: PaginatedTrailers = yield call(trailersApi.getTrailersPaginated, action.payload)
    yield put(assetsActions.fetchTrailersSuccess(result))
  } catch (error) {
    yield put(assetsActions.fetchTrailersFailure(extractErrorMessage(error)))
  }
}

function* fetchTrailerSaga(action: PayloadAction<string>) {
  try {
    const trailer: SagaReturnType<typeof trailersApi.getTrailer> = yield call(
      trailersApi.getTrailer,
      action.payload
    )
    if (trailer) {
      yield put(assetsActions.fetchTrailerSuccess(trailer))
    } else {
      yield put(assetsActions.fetchTrailerFailure('assets.errors.trailerNotFound'))
    }
  } catch (error) {
    yield put(assetsActions.fetchTrailerFailure(extractErrorMessage(error)))
  }
}

function* createTrailerSaga(action: PayloadAction<Partial<Trailer> & { tenantId: string }>) {
  try {
    const trailer: SagaReturnType<typeof trailersApi.createTrailer> = yield call(
      trailersApi.createTrailer,
      action.payload
    )
    yield put(assetsActions.createTrailerSuccess(trailer))
    yield put(assetsActions.fetchStats())
  } catch (error) {
    yield put(assetsActions.createTrailerFailure(extractErrorMessage(error)))
  }
}

function* updateTrailerSaga(
  action: PayloadAction<{ documentId: string; data: Partial<Trailer> }>
) {
  try {
    const result: SagaReturnType<typeof trailersApi.updateTrailer> = yield call(
      trailersApi.updateTrailer,
      action.payload.documentId,
      action.payload.data
    )
    yield put(assetsActions.updateTrailerSuccess(result.entity))
    yield put(assetsActions.fetchStats())
    if (result.deactivation.affectedOrders.length > 0) {
      yield put(assetsActions.showDeactivationWarning({
        affectedOrders: result.deactivation.affectedOrders,
        affectedResources: result.deactivation.affectedResources,
        entityType: 'trailer',
        entityName: (result.entity as any).licensePlate ?? null,
      }))
    }
  } catch (error) {
    yield put(assetsActions.updateTrailerFailure(extractErrorMessage(error)))
  }
}

function* deleteTrailerSaga(action: PayloadAction<string>) {
  try {
    yield call(trailersApi.deleteTrailer, action.payload)
    yield put(assetsActions.deleteTrailerSuccess(action.payload))
    yield put(assetsActions.fetchStats())
  } catch (error) {
    yield put(assetsActions.deleteTrailerFailure(extractErrorMessage(error)))
  }
}

// ============================================================================
// Vehicles Sagas
// ============================================================================

function* fetchVehiclesSaga(action: PayloadAction<VehiclesFilter | undefined>) {
  try {
    const result: PaginatedVehicles = yield call(vehiclesApi.getVehiclesPaginated, action.payload)
    yield put(assetsActions.fetchVehiclesSuccess(result))
  } catch (error) {
    yield put(assetsActions.fetchVehiclesFailure(extractErrorMessage(error)))
  }
}

function* fetchVehicleSaga(action: PayloadAction<string>) {
  try {
    const vehicle: SagaReturnType<typeof vehiclesApi.getVehicle> = yield call(
      vehiclesApi.getVehicle,
      action.payload
    )
    if (vehicle) {
      yield put(assetsActions.fetchVehicleSuccess(vehicle))
    } else {
      yield put(assetsActions.fetchVehicleFailure('assets.errors.vehicleNotFound'))
    }
  } catch (error) {
    yield put(assetsActions.fetchVehicleFailure(extractErrorMessage(error)))
  }
}

function* createVehicleSaga(action: PayloadAction<Partial<Vehicle> & { tenantId: string }>) {
  try {
    const vehicle: SagaReturnType<typeof vehiclesApi.createVehicle> = yield call(
      vehiclesApi.createVehicle,
      action.payload
    )
    yield put(assetsActions.createVehicleSuccess(vehicle))
    yield put(assetsActions.fetchStats())
  } catch (error) {
    yield put(assetsActions.createVehicleFailure(extractErrorMessage(error)))
  }
}

function* updateVehicleSaga(action: PayloadAction<{ documentId: string; data: Partial<Vehicle> }>) {
  try {
    const result: SagaReturnType<typeof vehiclesApi.updateVehicle> = yield call(
      vehiclesApi.updateVehicle,
      action.payload.documentId,
      action.payload.data
    )
    yield put(assetsActions.updateVehicleSuccess(result.entity))
    yield put(assetsActions.fetchStats())
    if (result.deactivation.affectedOrders.length > 0) {
      yield put(assetsActions.showDeactivationWarning({
        affectedOrders: result.deactivation.affectedOrders,
        affectedResources: result.deactivation.affectedResources,
        entityType: 'vehicle',
        entityName: (result.entity as any).licensePlate ?? null,
      }))
    }
  } catch (error) {
    yield put(assetsActions.updateVehicleFailure(extractErrorMessage(error)))
  }
}

function* deleteVehicleSaga(action: PayloadAction<string>) {
  try {
    yield call(vehiclesApi.deleteVehicle, action.payload)
    yield put(assetsActions.deleteVehicleSuccess(action.payload))
    yield put(assetsActions.fetchStats())
  } catch (error) {
    yield put(assetsActions.deleteVehicleFailure(extractErrorMessage(error)))
  }
}

// ============================================================================
// Root Saga
// ============================================================================

export function* assetsSaga() {
  yield all([
    takeLatest(assetsActions.fetchAssets.type, fetchAssetsSaga),
    takeLatest(assetsActions.fetchStats.type, fetchFleetStatsSaga),
    takeLatest(assetsActions.fetchDrivers.type, fetchDriversSaga),
    takeLatest(assetsActions.fetchDriver.type, fetchDriverSaga),
    takeLatest(assetsActions.createDriver.type, createDriverSaga),
    takeLatest(assetsActions.updateDriverAction.type, updateDriverSaga),
    takeLatest(assetsActions.deleteDriverAction.type, deleteDriverSaga),
    takeLatest(assetsActions.fetchResources.type, fetchResourcesSaga),
    takeLatest(assetsActions.fetchResource.type, fetchResourceSaga),
    takeLatest(assetsActions.createResource.type, createResourceSaga),
    takeLatest(assetsActions.updateResourceAction.type, updateResourceSaga),
    takeLatest(assetsActions.deleteResourceAction.type, deleteResourceSaga),
    takeLatest(assetsActions.fetchVehicles.type, fetchVehiclesSaga),
    takeLatest(assetsActions.fetchVehicle.type, fetchVehicleSaga),
    takeLatest(assetsActions.createVehicle.type, createVehicleSaga),
    takeLatest(assetsActions.updateVehicleAction.type, updateVehicleSaga),
    takeLatest(assetsActions.deleteVehicleAction.type, deleteVehicleSaga),
    takeLatest(assetsActions.fetchTrailers.type, fetchTrailersSaga),
    takeLatest(assetsActions.fetchTrailer.type, fetchTrailerSaga),
    takeLatest(assetsActions.createTrailer.type, createTrailerSaga),
    takeLatest(assetsActions.updateTrailerAction.type, updateTrailerSaga),
    takeLatest(assetsActions.deleteTrailer.type, deleteTrailerSaga),
  ])
}
